import { Avatar, AvatarFallback, AvatarImage } from "@gigpro/ui/Avatar";
import { Dialog, DialogClose } from "@gigpro/ui/Dialog";
import { Zoom } from "@gigpro/ui/Icons";
import { cn } from "@gigpro/ui/tailwind/cn";
import React from "react";
import { getCloudinaryImage } from "utils/cloudinary";

type ProAvatarProps = {
  ref?: React.Ref<HTMLDivElement>;
  imageId?: string | null;
  /** Pro display name. When set with no `imageId`, renders pastel-green initials instead of the generic profile icon. */
  displayName?: string | null;
  size?: number;
  className?: string;
  disableZoom?: boolean;
};

const zoomedImageSize = 512;

const WHITESPACE = /\s+/;

function pickInitials(displayName: string): string {
  return displayName
    .trim()
    .split(WHITESPACE)
    .map((w) => w[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export const ProAvatar = ({ ref, imageId, displayName, size = 40, disableZoom, className }: ProAvatarProps) => {
  const [isZoomed, setIsZoomed] = React.useState(false);

  const avatarProps = {
    ref,
    className: cn("size-(--size) bg-medium-gray text-white", disableZoom && "pointer-events-none", className),
    style: { "--size": `${size}px` } as React.CSSProperties,
  };
  const initials = displayName ? pickInitials(displayName) : "";
  const initialsFallback = initials ? (
    <AvatarFallback
      className="bg-accent-light-green font-semibold text-success"
      style={{ fontSize: `${Math.round(size * 0.42)}px` }}
    >
      {initials}
    </AvatarFallback>
  ) : (
    <AvatarFallback />
  );
  return imageId ? (
    <>
      <Avatar
        {...avatarProps}
        onClick={
          disableZoom
            ? undefined
            : (e) => {
                e.stopPropagation();
                setIsZoomed(true);
              }
        }
      >
        <AvatarImage
          src={getCloudinaryImage(imageId, { width: size, height: size, circle: true }).toURL()}
          alt="Headshot"
          loading="lazy"
          className="size-full"
        />
        {initialsFallback}
        {!disableZoom && (
          <div className="absolute inset-0 z-10 grid size-full place-items-center rounded-full bg-black/20 opacity-0 transition-all duration-300 hover:opacity-100">
            <Zoom className="size-3/4 text-white" />
          </div>
        )}
      </Avatar>
      {!disableZoom && (
        <Dialog
          open={isZoomed}
          onOpenChange={setIsZoomed}
          bottomSheet
          closeOnBackdropClick
          blockInteraction
          contentClassName="gap-0 p-0"
        >
          <div className="ml-auto pt-2 pr-2">
            <DialogClose />
          </div>
          <img
            alt="Headshot"
            className="size-full sm:rounded-b"
            width={zoomedImageSize}
            height={zoomedImageSize}
            src={getCloudinaryImage(imageId, { width: zoomedImageSize, height: zoomedImageSize }).toURL()}
          />
        </Dialog>
      )}
    </>
  ) : (
    <Avatar {...avatarProps}>{initialsFallback}</Avatar>
  );
};
